import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';

import type { FragmentType } from '../gql/fragment-masking';

// Helper alias for readability
type Doc<T> = DocumentTypeDecoration<T, unknown>;

// The validation is expressed in the `fragmentRef` parameter type rather than as a type
// parameter constraint. This avoids circular inference: TypeScript first infers T1...TN freely
// from `fragments`, then evaluates the conditional to check chain compatibility.
// If fragment[N] is not spread inside fragment[N-1], `fragmentRef` resolves to `never`,
// producing a clear call-site error.

// --- 2 levels ---

export function drillFragmentData<T1, T2>(
  fragments: readonly [Doc<T1>, Doc<T2>],
  fragmentRef: [T1] extends [FragmentType<Doc<T2>>]
    ? FragmentType<Doc<T1>>
    : never,
): T2;

export function drillFragmentData<T1, T2>(
  fragments: readonly [Doc<T1>, Doc<T2>],
  fragmentRef: [T1] extends [FragmentType<Doc<T2>>]
    ? FragmentType<Doc<T1>> | null | undefined
    : never,
): T2 | null | undefined;

// --- 3 levels ---

export function drillFragmentData<T1, T2, T3>(
  fragments: readonly [Doc<T1>, Doc<T2>, Doc<T3>],
  fragmentRef: [T1] extends [FragmentType<Doc<T2>>]
    ? [T2] extends [FragmentType<Doc<T3>>]
      ? FragmentType<Doc<T1>>
      : never
    : never,
): T3;

export function drillFragmentData<T1, T2, T3>(
  fragments: readonly [Doc<T1>, Doc<T2>, Doc<T3>],
  fragmentRef: [T1] extends [FragmentType<Doc<T2>>]
    ? [T2] extends [FragmentType<Doc<T3>>]
      ? FragmentType<Doc<T1>> | null | undefined
      : never
    : never,
): T3 | null | undefined;

// --- 4 levels ---

export function drillFragmentData<T1, T2, T3, T4>(
  fragments: readonly [Doc<T1>, Doc<T2>, Doc<T3>, Doc<T4>],
  fragmentRef: [T1] extends [FragmentType<Doc<T2>>]
    ? [T2] extends [FragmentType<Doc<T3>>]
      ? [T3] extends [FragmentType<Doc<T4>>]
        ? FragmentType<Doc<T1>>
        : never
      : never
    : never,
): T4;

export function drillFragmentData<T1, T2, T3, T4>(
  fragments: readonly [Doc<T1>, Doc<T2>, Doc<T3>, Doc<T4>],
  fragmentRef: [T1] extends [FragmentType<Doc<T2>>]
    ? [T2] extends [FragmentType<Doc<T3>>]
      ? [T3] extends [FragmentType<Doc<T4>>]
        ? FragmentType<Doc<T1>> | null | undefined
        : never
      : never
    : never,
): T4 | null | undefined;

// --- implementation - no-op at runtime, type-level only (same as getFragmentData) ---

export function drillFragmentData(
  _fragments: readonly DocumentTypeDecoration<unknown, unknown>[],
  fragmentRef: unknown,
): unknown {
  return fragmentRef;
}
